// Feed Munchy — pure game logic and constants (no React).
//
// Munchy asks for a snack by NAME, COLOR, or CATEGORY and the child drags the
// matching item into his mouth. Rounds are generated so exactly one item on the
// tray can ever satisfy the request, so a pre-reader who matches by sight is
// always right. Eight snacks make a meal.

const ASSET_BASE = "/assets/games/feed-monster";
const asset = (name) => `${ASSET_BASE}/${name}.png`;

// Swatch tints shown beside a color request so pre-readers can match by sight.
export const COLOR_DISPLAY = {
  red: "#ED4D4D",
  orange: "#FF9933",
  yellow: "#FFC729",
  green: "#40B866",
  blue: "#408CF2",
  purple: "#9973E6",
  pink: "#FF8CBF",
  brown: "#9E7047",
};

// A familiar same-color thing, used in the stronger second-miss hints
// ("red like a fire truck!").
export const COLOR_ASSOCIATION = {
  red: "a fire truck",
  orange: "a pumpkin",
  yellow: "the sun",
  green: "the grass",
  blue: "the sky",
  purple: "a juicy plum",
  pink: "a flamingo",
  brown: "a teddy bear",
};

// How Munchy asks for a whole category out loud.
export const CATEGORY_PHRASE = {
  fruit: "a juicy fruit",
  veggie: "a crunchy veggie",
  treat: "a sweet treat",
};

// The pantry — everything Munchy might ask for. Requests are generated so
// exactly ONE tray item satisfies them, so repeated colors/categories in the
// pool are fine (the factory keeps them off the same tray).
export const PANTRY = [
  // Fruits
  { id: "apple", asset: asset("fm_item_apple"), label: "Apple", color: "red", category: "fruit" },
  { id: "banana", asset: asset("fm_item_banana"), label: "Banana", color: "yellow", category: "fruit" },
  { id: "orange", asset: asset("fm_item_orange"), label: "Orange", color: "orange", category: "fruit" },
  { id: "grapes", asset: asset("fm_item_grapes"), label: "Grapes", color: "purple", category: "fruit" },
  { id: "pear", asset: asset("fm_item_pear"), label: "Pear", color: "green", category: "fruit" },
  { id: "blueberries", asset: asset("fm_item_blueberries"), label: "Blueberries", color: "blue", category: "fruit" },
  // Veggies
  { id: "carrot", asset: asset("fm_item_carrot"), label: "Carrot", color: "orange", category: "veggie" },
  { id: "broccoli", asset: asset("fm_item_broccoli"), label: "Broccoli", color: "green", category: "veggie" },
  { id: "corn", asset: asset("fm_item_corn"), label: "Corn", color: "yellow", category: "veggie" },
  { id: "potato", asset: asset("fm_item_potato"), label: "Potato", color: "brown", category: "veggie" },
  // Treats
  { id: "cookie", asset: asset("fm_item_cookie"), label: "Cookie", color: "brown", category: "treat" },
  { id: "cupcake", asset: asset("fm_item_cupcake"), label: "Cupcake", color: "pink", category: "treat" },
  { id: "icepop", asset: asset("fm_item_icepop"), label: "Ice pop", color: "blue", category: "treat" },
  { id: "donut", asset: asset("fm_item_donut"), label: "Donut", color: "pink", category: "treat" },
];

export const ROUNDS_PER_MEAL = 8;
export const MAX_TRAY_LEVEL = 2;
export const REQUEST_KINDS = ["name", "color", "category"];

// Spoken script (voiced with the goofy Munchy pitch), ported verbatim from the
// iOS `MonsterVoice`. The FIRST intro is the canonical "I'm Munchy!" name
// introduction — always used the very first time a child meets him; after that
// the greeting is randomized so hellos stay fresh.
export const INTRO_LINES = [
  "Hi hi! I'm Munchy! My tummy is SO rumbly. Will you feed me the snacks I ask for?",
  "You're back, you're back! Munchy's tummy was JUST asking about you!",
  "GRUMBLE grumble… hear that? That's my tummy talking! Quick, snack time!",
  "Ooh, hello friend! Munchy dreamed about yummy snacks all night. Feed me, please?",
  "It's snack time, it's snack time! Munchy is SO hungry today!",
];

// The classic greeting stays the default (first thing displayed if nothing has
// been picked yet, and used by older callers).
export const INTRO_LINE = INTRO_LINES[0];

// The first-ever open gets the canonical "I'm Munchy!" hello; afterwards a
// random greeting keeps game openings fresh (mirrors iOS `fm_met_munchy`).
export const pickIntroLine = (hasMetMunchy = true, random = Math.random) =>
  hasMetMunchy ? pick(INTRO_LINES, random) : INTRO_LINES[0];

export const FULL_LINE =
  "BURP! Oh, excuse me! Munchy's tummy is all full. Thank you, friend!";

export const PRAISE_LINES = [
  "CHOMP! Delicious!",
  "Nom nom nom! Thank you!",
  "Mmm! Yummy in my tummy!",
  "That hit the spot!",
  "So tasty! You're the best!",
  "WOWIE! That was scrumptious!",
  "Munch munch munch… mmm, perfect!",
  "Ooh, my favorite! How did you know?",
  "GULP! More please, more please!",
  "Yum yum YUM! You're such a good helper!",
  "Tee-hee! That tickled my tummy!",
  "Munchy is one happy monster!",
  "Sooo yummy! My tummy is doing a happy dance!",
];

// Spoken instead of a regular praise when a first-try streak hits 3/5/8.
export const STREAK_LINES = [
  "Wow wow WOW! You're a SUPER listener!",
  "Right again and again! My tummy is so lucky!",
  "You get it right every time! Amazing!",
];

// Spoken instead of a praise the moment the meal reaches its halfway point.
export const HALFWAY_LINES = [
  "Ooh ooh! My tummy is HALFWAY full! Keep going, friend!",
  "Half the meal already? You're a super feeder!",
];

// The whole meal on the first try — the biggest cheer Munchy has.
export const PERFECT_LINE =
  "PERFECT! Every single snack on the very first try! Munchy LOVES you!";

export const STREAK_MILESTONES = [3, 5, 8];

export const spokenName = (item) => item.label.toLowerCase();

export const shuffle = (items, random = Math.random) => {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
  }
  return result;
};

const pick = (items, random = Math.random) =>
  items[Math.min(items.length - 1, Math.floor(random() * items.length))];

// True when `item` is a correct answer for `round`.
export const roundSatisfies = (round, item) => {
  if (!round || !item) return false;
  switch (round.kind) {
    case "name":
      return item.id === round.target.id;
    case "color":
      return item.color === round.target.color;
    case "category":
      return item.category === round.target.category;
    default:
      return false;
  }
};

// Munchy's request line, spoken and shown. Never a raw score — just an ask.
export const promptLine = (kind, target, random = Math.random) => {
  switch (kind) {
    case "name":
      return pick(
        [
          `Munchy wants the ${spokenName(target)}! Nom nom!`,
          `Please feed me the ${spokenName(target)}!`,
          `Ooh, the ${spokenName(target)} looks SO yummy!`,
        ],
        random
      );
    case "color":
      return pick(
        [
          `Munchy wants something ${target.color}!`,
          `Feed me something ${target.color}, please!`,
          `My tummy is asking for something ${target.color}!`,
        ],
        random
      );
    case "category":
    default:
      return pick(
        [
          `Munchy is hungry for ${CATEGORY_PHRASE[target.category]}!`,
          `Feed me ${CATEGORY_PHRASE[target.category]}, please!`,
          `Mmm, ${CATEGORY_PHRASE[target.category]} sounds perfect!`,
        ],
        random
      );
  }
};

// Gentle spoken redirect for a wrong feed — silly, never mean, and always
// restates what Munchy asked for. Never a "wrong!" or fail state.
export const nudgeLine = (round, wrong) => {
  switch (round.kind) {
    case "name":
      return `Bleh — that's the ${spokenName(wrong)}! Munchy wants the ${spokenName(round.target)}!`;
    case "color":
      return `Oops, the ${spokenName(wrong)} is ${wrong.color}! Munchy wants something ${round.target.color}!`;
    case "category":
    default:
      return `That's ${CATEGORY_PHRASE[wrong.category]}! Munchy wants ${CATEGORY_PHRASE[round.target.category]}!`;
  }
};

// Wrong feed during the follow-up half of a two-step order — always restates
// the follow-up snack (which is always asked by name, so it's unambiguous).
export const followUpNudge = (round, wrong) => {
  if (!round?.followUp) return nudgeLine(round, wrong);
  return `Bleh — that's the ${spokenName(wrong)}! Munchy asked for the ${spokenName(round.followUp)}!`;
};

// A stronger, more descriptive hint spoken after a second miss — paired with
// the glowing answer in the tray, so nobody ever stays stuck.
export const strongHint = (round) => {
  const { kind, target } = round;
  switch (kind) {
    case "name":
      return `Look for the ${spokenName(target)} — it's the ${target.color} one!`;
    case "color":
      return `Something ${target.color} — ${target.color} like ${COLOR_ASSOCIATION[target.color]}! Can you find it?`;
    case "category":
    default:
      return `Munchy wants ${CATEGORY_PHRASE[target.category]} — like the ${spokenName(target)}! Find the ${spokenName(target)}!`;
  }
};

export const followUpStrongHint = (round) => {
  if (!round?.followUp) return strongHint(round);
  return `Find the ${spokenName(round.followUp)} — it's the ${round.followUp.color} one!`;
};

// The follow-up ask line for a two-step order ("…AND NOW the banana!").
export const followUpPromptLine = (item, random = Math.random) =>
  pick(
    [
      `Mmm! AND NOW… the ${spokenName(item)}, please!`,
      `More, more! Next, feed me the ${spokenName(item)}!`,
      `That was part one! Now the ${spokenName(item)}!`,
    ],
    random
  );

// Total snacks across a meal: two-step rounds serve two, so the tummy meter
// counts snacks, not rounds.
export const totalSnacks = (rounds) =>
  (rounds || []).reduce((sum, round) => sum + 1 + (round.followUp ? 1 : 0), 0);

// True when the item satisfies whatever Munchy is asking for right now — the
// round's request during part one, or the exact follow-up snack during part two.
export const askSatisfied = (round, item, inFollowUp) => {
  if (!round || !item) return false;
  if (inFollowUp) return item.id === round.followUp?.id;
  return roundSatisfies(round, item);
};

// How many items to put on the tray for this round at a given difficulty level.
// Level 0: first 2 rounds 3 items else 4. Level 1: always 4. Level 2: first 2
// rounds 4 else 5. Trays grow as the meal (and the child's skill) progresses.
export const traySizeFor = (level, index) => {
  if (level >= MAX_TRAY_LEVEL) return index < 2 ? 4 : 5;
  if (level === 1) return 4;
  return index < 2 ? 3 : 4;
};

// One full meal: a shuffled mix of name / color / category requests, no
// repeated target, always starting with an easy name round. `level` (0…2) is
// the persisted difficulty carried between sessions.
export const makeSession = (rounds = ROUNDS_PER_MEAL, level = 0, random = Math.random) => {
  const kinds = shuffle(
    ["name", "name", "color", "color", "color", "category", "category"],
    random
  );
  kinds.unshift("name");
  while (kinds.length < rounds) kinds.push(pick(REQUEST_KINDS, random));
  const chosenKinds = kinds.slice(0, rounds);

  const usedTargets = new Set();

  const session = chosenKinds.map((kind, index) => {
    const traySize = traySizeFor(level, index);

    const available = PANTRY.filter((item) => !usedTargets.has(item.id));
    const target = (available.length ? pick(available, random) : pick(PANTRY, random));
    usedTargets.add(target.id);

    // Distractors that can NOT also satisfy the request, so only the target is
    // ever correct.
    let distractorPool;
    if (kind === "name") {
      distractorPool = PANTRY.filter((item) => item.id !== target.id);
    } else if (kind === "color") {
      distractorPool = PANTRY.filter((item) => item.color !== target.color);
    } else {
      distractorPool = PANTRY.filter((item) => item.category !== target.category);
    }

    const options = shuffle(
      [...shuffle(distractorPool, random).slice(0, traySize - 1), target],
      random
    );

    return {
      id: `round-${index}-${target.id}`,
      kind,
      target,
      options,
      prompt: promptLine(kind, target, random),
      swatch: kind === "color" ? COLOR_DISPLAY[target.color] : null,
      followUp: null,
      followUpPrompt: null,
    };
  });

  // Top difficulty: a couple of two-step orders — the child holds TWO
  // instructions in mind ("first the request, THEN the banana"). The follow-up
  // is an existing tray item that does NOT satisfy the first ask, always asked
  // by name so it's unambiguous.
  if (level >= MAX_TRAY_LEVEL) {
    const candidates = shuffle(
      session.map((_, index) => index).filter((index) => index >= 2),
      random
    ).slice(0, 2);
    candidates.forEach((index) => {
      const round = session[index];
      const extraPool = round.options.filter((item) => !roundSatisfies(round, item));
      if (!extraPool.length) return;
      const extra = pick(extraPool, random);
      round.followUp = extra;
      round.followUpPrompt = followUpPromptLine(extra, random);
    });
  }

  return session;
};

// Child-facing score is never shown as a raw number mid-game; this only feeds
// the results summary and backend save.
export const finalScore = (firstTryCount, total = ROUNDS_PER_MEAL) =>
  total === 0 ? 0 : Math.round((firstTryCount * 100) / total);

// Earned difficulty, persisted across sessions: a perfect meal grows the tray
// next time, a rough one (half or fewer on the first try) shrinks it back.
export const trayLevelAfterMeal = (level, firstTryCount, total = ROUNDS_PER_MEAL) => {
  if (firstTryCount >= total) return Math.min(MAX_TRAY_LEVEL, level + 1);
  if (firstTryCount <= Math.floor(total / 2)) return Math.max(0, level - 1);
  return level;
};
